import { useQuery, useQueries } from '@tanstack/react-query';
import axios from 'axios';
import { getCurrentUserId } from '../utils/userUtils';

const API_BASE_URL = 'https://plansync-backend-234288438538.us-central1.run.app/api';

// Fetch functions
const fetchDashboardData = async (userId, year) => {
  const response = await axios.get(`${API_BASE_URL}/dashboard/${userId}?year=${year}`);
  return response.data;
};

const fetchTimeData = async (userId, year) => {
  const response = await axios.get(`${API_BASE_URL}/get_time_data/${year}?user_id=${userId}`);
  return response.data;
};

const fetchClientList = async (userId, year) => {
  const response = await axios.get(`${API_BASE_URL}/clients/${userId}/${year}`);
  return response.data;
};

const fetchUserYears = async (userId) => {
  const response = await axios.get(`${API_BASE_URL}/user_years/${userId}`);
  // Ensure years are returned as an array of numbers
  const years = response.data.years || [];
  return years.map(year => parseInt(year, 10)).sort((a, b) => a - b);
};

const fetchTouchpointCounts = async (userId, year) => {
  const response = await axios.get(
    `${API_BASE_URL}/touchpoint-counts-summary/${year}`,
    {
      headers: { 'user-id': userId }
    }
  );
  return response.data;
};

const fetchRequirementsCounts = async (userId, year, data) => {
  const response = await axios({
    method: 'post',
    url: `${API_BASE_URL}/checklist-requirements-counts/${year}`,
    data: {
      selected_companies: data.selected_companies,
      include_all_clients_in_comparison: data.include_all_clients_in_comparison
    },
    headers: {
      'user-id': userId,
      'Content-Type': 'application/json'
    }
  });
  return response.data;
};

// Custom hook for dashboard data
export const useDashboardQuery = (selectedYear) => {
  const userId = getCurrentUserId();

  // Query for user years
  const {
    data: availableYears = [],
    isLoading: isLoadingYears,
    error: yearsError
  } = useQuery({
    queryKey: ['dashboard', 'userYears', userId],
    queryFn: () => fetchUserYears(userId),
    enabled: !!userId,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
  });

  // Main dashboard queries
  const dashboardQueries = useQueries({
    queries: [
      {
        queryKey: ['dashboard', 'dashboard', selectedYear, userId],
        queryFn: () => fetchDashboardData(userId, selectedYear),
        enabled: !!userId && !!selectedYear,
        staleTime: 30 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
      },
      {
        queryKey: ['dashboard', 'timeData', selectedYear, userId],
        queryFn: () => fetchTimeData(userId, selectedYear),
        enabled: !!userId && !!selectedYear,
        staleTime: 30 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
      },
      {
        queryKey: ['dashboard', 'clientList', selectedYear, userId],
        queryFn: () => fetchClientList(userId, selectedYear),
        enabled: !!userId && !!selectedYear,
        staleTime: 30 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
      },
      {
        queryKey: ['dashboard', 'touchpointCounts', selectedYear, userId],
        queryFn: () => fetchTouchpointCounts(userId, selectedYear),
        enabled: !!userId && !!selectedYear,
        staleTime: 30 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
      }
    ]
  });

  // Previous year queries
  const previousYear = selectedYear - 1;
  const hasPreviousYear = Array.isArray(availableYears) && availableYears.includes(previousYear);
  
  const previousYearQueries = useQueries({
    queries: [
      {
        queryKey: ['dashboard', 'dashboard', previousYear, userId],
        queryFn: () => fetchDashboardData(userId, previousYear),
        enabled: !!userId && hasPreviousYear,
        staleTime: 30 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
      },
      {
        queryKey: ['dashboard', 'clientList', previousYear, userId],
        queryFn: () => fetchClientList(userId, previousYear),
        enabled: !!userId && hasPreviousYear,
        staleTime: 30 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
      }
    ]
  });

  // Requirements counts query (depends on dashboard data)
  const dashboardData = dashboardQueries[0]?.data;
  const {
    data: requirementsData,
    isLoading: isLoadingRequirements,
    error: requirementsError
  } = useQuery({
    queryKey: ['dashboard', 'requirements', selectedYear, userId, dashboardData?.plan_performance?.map(p => p.company_name)],
    queryFn: () => fetchRequirementsCounts(userId, selectedYear, {
      selected_companies: dashboardData.plan_performance.map(plan => plan.company_name),
      include_all_clients_in_comparison: true
    }),
    enabled: !!userId && !!selectedYear && !!dashboardData?.plan_performance,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
  });

  // Extract data from queries
  const [dashboardQuery, timeDataQuery, clientListQuery, touchpointQuery] = dashboardQueries;
  const [previousDashboardQuery, previousClientListQuery] = previousYearQueries;

  const isLoading = isLoadingYears || dashboardQueries.some(q => q.isLoading);
  const error = yearsError || dashboardQueries.find(q => q.error)?.error;

  return {
    // Current year data
    dashboardData: dashboardQuery.data,
    timeData: timeDataQuery.data,
    clientListData: clientListQuery.data,
    touchpointData: touchpointQuery.data,
    requirementsData,
    
    // Previous year data
    previousYearData: previousDashboardQuery.data,
    previousYearClientList: previousClientListQuery.data,
    
    // Meta
    availableYears,
    isLoading,
    isLoadingRequirements,
    error: error || requirementsError,
    
    // Refetch functions
    refetchDashboard: () => dashboardQueries.forEach(q => q.refetch()),
    refetchPreviousYear: () => previousYearQueries.forEach(q => q.refetch()),
  };
};